
import { gsap } from "gsap";
import { Presentation } from "../../../dev/gameflow/controller/Presentation";
import { StateHandler } from "../../../dev/utils/StateHandler";
import { WaysCounter } from "../../independent/countup/WaysCounter";
import { RoadRageEngineManager } from "../../reel/RoadRageEngineManager";
import { GameData } from "../../server/data/BaseModel";
import { CarMoveInfo, CarTypes } from '../../server/data/GameDataModels';
import { ArrayHelper } from '../../../dev/utils/ArrayHelper';
import { RoadRageSpriteSymbol } from '../../reel/symbol/RoadRageSpriteSymbol';
import { GameMode } from '@nolimitcity/slot-game/bin/flowcontrol/GameMode';
import { RoadRage } from '../../RoadRage';
import { GameSetting } from '../../../dev/setting/GameSetting';
import { SoundConfig } from '../../config/sound/SoundConfig';
import { SoundManager } from '../../config/sound/SoundManager';
import { SpinePlayer } from '../../../dev/displayobject/SpinePlayer';

class featureStates extends StateHandler {
    public static START_CARS: string = "start";
    public static MAD_EVENT: string = "madEvent";
    public static MYSTERY: string = "mystery";
    public static DONE: string = "done";
}

export class FeatureFlow extends Presentation {

    private gameData: GameData;
    private state: featureStates = new featureStates();
    private carInfo: CarMoveInfo[] = [];
    private collidedCars: CarMoveInfo[] = [];

    private isFS: boolean = false;

    setGamedData(data: GameData): void {
        this.gameData = data;
        this.carInfo = this.gameData.Reel.carMoveInfo;

        this.isFS = data.mode === GameMode.FREESPIN;

        this.collidedCars = this.carInfo.filter(c => c.collides);
        let timeChecks: number = (this.collidedCars.length / 2);
        if (this.collidedCars.length === 1) {
            this.collidedCars[0].collides = false;
        }
        else {
            for (let t = 0; t < timeChecks; t++) {
                ArrayHelper.removeFirstMatchElementWithCondition((this.collidedCars), (v) => {
                    return v.reelTo === this.collidedCars[0].reelTo && v.row === this.collidedCars[0].row;
                });

                if (this.collidedCars.length) {
                    this.collidedCars[0].value = 2;
                    ArrayHelper.swapPositions(this.collidedCars, 0, this.collidedCars.length - 1);
                }
            }
        }
    }

    preStart(): void {
        this.state.set(featureStates.START_CARS);
    }

    checkForActivation(data: GameData) {
        return !!data.Reel.carMoveInfo.length;
    }

    createPresentation(enableSkip: Function) {
        enableSkip(true);

        switch (this.state.value) {
            case featureStates.START_CARS:
                this.driveCars();
                if (this.timeline.duration())
                    WaysCounter.instance.setValue(undefined, this.gameData.Reel.amountOfWaysOfSymbolSize, Math.max(0.1, this.timeline.duration() - 0.1));
                this.state.set(featureStates.MAD_EVENT);
                break;
            case featureStates.MAD_EVENT:
                this.madness();
                this.state.set(featureStates.DONE);
                break;
            default:
                this.PresentationState.setInactive();
                break;
        }
        if (this.timeline.totalDuration() > 0)
            this.timeline.to(this, { duration: 0.1 })
    }

    driveCars(): void {
        let soundPlayed:boolean = false;
        let sweared:boolean = false;
        let collided:boolean = false;
        let crashed:boolean = false;
        let moved:boolean = false;

        const soundLock:boolean[] = [true,true,true,true,true];

        this.carInfo.forEach(cI => {
            const reelSteps: number = cI.reelTo - cI.reel;

            let isSticky:boolean = this.gameData.carsActiveInFS.indexOf(cI.type) >= 0;
            if(cI.collides && !isSticky){
                isSticky = this.gameData.carsActiveInFS.indexOf("WC1") >= 0 || this.gameData.carsActiveInFS.indexOf("WC2") >= 0;
            }

            if(Math.abs(reelSteps) > 0) moved = true;

            const moveSpeed: number = GameSetting.isFastSpin ? 0.33 : 0.5;
            const moveTL: gsap.core.Timeline = RoadRageEngineManager.getReel(cI.reel).getSymbol(cI.row)?.CarMoveTo(reelSteps, moveSpeed)!;
            const moveDurr: number = moveTL.duration();
            const splitVal: number = this.isFS ? Math.max(1,RoadRage.bgOverlay.fetchBoxBytype(cI.type).splitValue) : 1;

            let splitDelay: number = (moveSpeed - 0.1);
            if (reelSteps < 0) {
                for (let i = 0; i < Math.abs(reelSteps) - 1; i++) {
                    let playSound = soundLock[i];
                    soundLock[i] = false;
                    moveTL.add(RoadRageEngineManager.getReel(cI.reel - (i + 1)).getSymbol(cI.row)!.addMult(splitVal, cI.type, true, playSound ), splitDelay);
                    moveTL.add(() => {RoadRageEngineManager.getReel(cI.reel - (i + 1)).getSymbol(cI.row)!.Explosion() }, splitDelay+0.1);
                    
                    splitDelay += 0.07;
                }
            } else {
                for (let i = 0; i < reelSteps - 1; i++) {
                    let playSound = soundLock[i];
                    soundLock[i] = false;
                    moveTL.add(RoadRageEngineManager.getReel(cI.reel + (i + 1)).getSymbol(cI.row)!.addMult(splitVal, cI.type, true, playSound), splitDelay);
                    moveTL.add(() => {RoadRageEngineManager.getReel(cI.reel + (i + 1)).getSymbol(cI.row)!.Explosion() }, splitDelay+0.1);

                    splitDelay += 0.07;
                }
            }

            const affectedSymPos: RoadRageSpriteSymbol = RoadRageEngineManager.getReel(cI.reelTo).getSymbol(cI.row)!;
            const willSwitch: boolean = !cI.collides || cI.collides && cI.type === "WC1";

            moveTL.add(() => {

                RoadRageEngineManager.getReel(cI.reelTo).getSymbol(cI.rowTo)?.markNonWin(false,0,0.05);
                RoadRageEngineManager.getReel(cI.reel).getSymbol(cI.row)?.changeCarToWild(cI.collides)!;
                
                if(!cI.collides && !soundPlayed){
                    SoundManager.playSoundEffect(SoundConfig.CAR_TRANSFORM_TO_WILD);
                    soundPlayed = true;
                } 

                if (!reelSteps && !sweared && !this.gameData.Win.singleWin){
                    sweared = true;
                    gsap.to(this, { duration: 0.15 }).eventCallback("onComplete", () => affectedSymPos.Swear());
                } 

                if (willSwitch) {
                    this.timeline.add(affectedSymPos.playWildSwitch({ state: "Wild", forceState: true, sym: cI.collides ? "WC4" : cI.type, duration: 0.01 }),moveDurr);
                    if (cI.collides) {
                        sweared = true;
                        collided = true;
                        gsap.to(this, { duration: 0.15 }).eventCallback("onComplete", () => affectedSymPos.Swear());
                        const val = (RoadRage.bgOverlay.fetchBoxBytype(CarTypes.RED).multValue + RoadRage.bgOverlay.fetchBoxBytype(CarTypes.GREEN).multValue);
                        affectedSymPos.addMult(val, undefined, true)
                        if(!crashed){
                            crashed = true;
                            SoundManager.playSoundEffect(SoundConfig.CAR_RG_CRASH);
                        }
                    }
                    else {
                        affectedSymPos.addMult( (RoadRage.bgOverlay.fetchBoxBytype(cI.type).multValue), undefined)
                    }
                }
            }, moveDurr)

            this.timeline.add(moveTL, 0);
            if(willSwitch)
                this.timeline.to(this,{duration:SpinePlayer.fetchDuration("WILD","Pop_WC_2_144")},moveDurr);
        })

        this.timeline.to(this, { duration: GameSetting.isFastSpin && !this.gameData.Win.singleWin ?  0.05 : 0.2 })

        if(collided)
            this.timeline.to(this, { duration: GameSetting.isFastSpin ?  0.35 : 0.6 })

        if(moved) SoundManager.playTakeOffSound();
            
    }

    madness(): void {
        const redFiltered = this.gameData.Reel.carMoveInfo.filter(v => { return v.type === "WC1" && v.reel != v.reelTo })
        let carMoveInfo:CarMoveInfo | undefined;
        for(let row:number = 0; row < this.gameData.Reel.reelModeHeight; row++){
            if(redFiltered.filter(reds => reds.row === row ).length === 3){
                carMoveInfo = redFiltered.filter(reds => reds.row === row ).sort((a,b) => a.reelTo - b.reelTo)[2];
                if(carMoveInfo.reelTo+1 === 3){
                    RoadRageEngineManager.getReel(3).AllWindowSymbols[carMoveInfo.rowTo].applyMultValue(this.gameData.Reel.symbolSizes[3][carMoveInfo.rowTo]);
                    RoadRageEngineManager.getReel(3).AllWindowSymbols[carMoveInfo.rowTo].Explosion();
                    debugger;
                }
            }
        }

        if(carMoveInfo)
            this.timeline.to(this,{duration:0.25});
        
    }

    skip():void{

        SoundManager.stopTakeOffSound();
        SoundManager.stopSoundEffect(SoundConfig.CAR_RG_CRASH);
        SoundManager.stopSoundEffect(SoundConfig.CAR_TRANSFORM_TO_WILD);

        super.skip();
    }

    done(): void {
        RoadRageEngineManager.RSWindow((r,s) => s.mergeSymbolMult());
    }
}